// Kitchen specialties management hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { KitchenSpecialty } from '@/types/entities'
import { isAssistantLoggedIn } from '@/lib/permissions'

export const specialtyKeys = {
  all: ['kitchenSpecialties'] as const,
  list: (restaurantId: string) => [...specialtyKeys.all, 'list', restaurantId] as const,
  menuItem: (menuItemId: string) => [...specialtyKeys.all, 'menuItem', menuItemId] as const,
}

/**
 * Fetch all kitchen specialties for a restaurant
 */
export function useKitchenSpecialties(restaurantId: string | null) {
  return useQuery({
    queryKey: specialtyKeys.list(restaurantId || ''),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    queryFn: async (): Promise<KitchenSpecialty[]> => {
      if (!restaurantId) {
        return []
      }

      // Assistants use server-side permission-checked RPC
      if (isAssistantLoggedIn()) {

        const { data, error } = await (supabase.rpc as any)('assistant_get_kitchen_specialties', {
          
          p_restaurant_id: restaurantId,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            return []
          }
          throw error
        }

        return (data || []).map((s: any) => ({
          id: s.id,
          restaurantId: s.restaurant_id,
          nameAr: s.name_ar,
          nameFr: s.name_fr,
          color: s.color,
          isActive: s.is_active,
          sortOrder: s.sort_order,
          notificationSound: s.notification_sound || 'notif1.mp3',
          createdAt: s.created_at,
          updatedAt: s.updated_at,
        }))
      }

      const { data, error } = await supabase.rpc('get_kitchen_specialties', { p_restaurant_id: restaurantId })

      if (error) {
        throw error
      }

      return (data || []).map((s: any) => ({
        id: s.id,
        restaurantId: s.restaurant_id,
        nameAr: s.name_ar,
        nameFr: s.name_fr,
        color: s.color,
        isActive: s.is_active,
        sortOrder: s.sort_order,
        notificationSound: s.notification_sound || 'notif1.mp3',
        createdAt: s.created_at,
        updatedAt: s.updated_at,
      }))
    },
    enabled: !!restaurantId,
  })
}

/**
 * Create a new kitchen specialty
 */
export function useCreateKitchenSpecialty(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      nameAr: string
      nameFr: string
      color?: string
      notificationSound?: string
    }) => {
      if (isAssistantLoggedIn()) {

        const { data, error } = await (supabase.rpc as any)('assistant_create_kitchen_specialty', {
          
          p_restaurant_id: restaurantId,
          p_name_ar: request.nameAr,
          p_name_fr: request.nameFr,
          p_color: request.color || '#6366f1',
          p_is_active: true,
          p_notification_sound: request.notificationSound || 'notif1.mp3',
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) throw new Error('Permission denied')
          throw error
        }

        return data
      }

      const { data, error } = await supabase.rpc('create_kitchen_specialty', {
        p_restaurant_id: restaurantId,
        p_name_ar: request.nameAr,
        p_name_fr: request.nameFr,
        p_color: request.color || '#6366f1',
        p_notification_sound: request.notificationSound || 'notif1.mp3',
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: specialtyKeys.list(restaurantId),
      })
    },
  })
}

/**
 * Update an existing kitchen specialty
 */
export function useUpdateKitchenSpecialty(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      specialtyId: string
      nameAr: string
      nameFr: string
      color: string
      isActive: boolean
      notificationSound: string
    }) => {
      if (isAssistantLoggedIn()) {

        const { data, error } = await (supabase.rpc as any)('assistant_update_kitchen_specialty', {
          
          p_specialty_id: request.specialtyId,
          p_restaurant_id: restaurantId,
          p_name_ar: request.nameAr,
          p_name_fr: request.nameFr,
          p_color: request.color,
          p_is_active: request.isActive,
          p_notification_sound: request.notificationSound,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) throw new Error('Permission denied')
          throw error
        }

        return data
      }

      const { data, error } = await supabase.rpc('update_kitchen_specialty', {
        p_specialty_id: request.specialtyId,
        p_name_ar: request.nameAr,
        p_name_fr: request.nameFr,
        p_color: request.color,
        p_is_active: request.isActive,
        p_notification_sound: request.notificationSound,
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: specialtyKeys.list(restaurantId),
      })
    },
  })
}

/**
 * Delete a kitchen specialty
 */
export function useDeleteKitchenSpecialty(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (specialtyId: string) => {
      if (isAssistantLoggedIn()) {

        const { data, error } = await (supabase.rpc as any)('assistant_delete_kitchen_specialty', {
          
          p_specialty_id: specialtyId,
          p_restaurant_id: restaurantId,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) throw new Error('Permission denied')
          throw error
        }

        return data
      }

      const { data, error } = await supabase.rpc('delete_kitchen_specialty', { p_specialty_id: specialtyId })

      if (error) {
        // Check if it's a foreign key constraint error
        if (error.code === '23503') {
          throw new Error('SPECIALTY_IN_USE')
        }
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: specialtyKeys.list(restaurantId),
      })
    },
  })
}

/**
 * Get specialties for a menu item
 */
export function useMenuItemSpecialties(menuItemId: string | null) {
  return useQuery({
    queryKey: specialtyKeys.menuItem(menuItemId || ''),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!menuItemId) {
        return []
      }

      const { data, error } = await supabase.rpc('get_menu_item_specialties', {
        p_menu_item_id: menuItemId,
      })

      if (error) {
        throw error
      }

      return (data || []).map((s: any) => ({
        specialtyId: s.specialty_id,
        nameAr: s.name_ar,
        nameFr: s.name_fr,
        color: s.color,
      }))
    },
    enabled: !!menuItemId,
  })
}

/**
 * Set specialties for a menu item
 */
export function useSetMenuItemSpecialties() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      menuItemId: string
      specialtyIds: string[]
    }) => {
      const { data, error } = await supabase.rpc('set_menu_item_specialties', {
        p_menu_item_id: request.menuItemId,
        p_specialty_ids: request.specialtyIds,
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: specialtyKeys.menuItem(variables.menuItemId),
      })
    },
  })
}
